import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/Icon';
import MeetingVideoModal from '../components/MeetingVideoModal';
import { joinState } from '../components/MeetingCalendar';
import { joinUrl } from '../utils/meet';

// Bugünüm: kişinin o günkü toplantıları (CRM e-postası ile eşleşen) ve açık görevleri, tek ekranda.
// Sıradaki toplantı en üstte; görevler aciliyete göre gruplu; sayfadan çıkmadan tamamla / yarına at / müşteride bekliyor.

const DAY_MS = 86400000;
const WORK_START = 9; const WORK_END = 19; // boş zaman blokları bu saatler arasında gösterilir
const MIN_GAP = 30; // dk

const hm = (d) => new Date(d).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
const endOf = (m) => {
  const s = new Date(m.meeting_date);
  if (m.end_at) { const e = new Date(m.end_at); if (e > s) return e; }
  return new Date(s.getTime() + (m.duration_minutes || 30) * 60000);
};
const addDays = (ymd, n) => { const d = new Date(`${ymd}T12:00:00Z`); return new Date(d.getTime() + n * DAY_MS).toISOString().slice(0, 10); };
const dayDiff = (a, b) => Math.round((new Date(`${a}T12:00:00Z`) - new Date(`${b}T12:00:00Z`)) / DAY_MS);
const longDate = (ymd) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
const dur = (min) => (min >= 60 ? `${Math.floor(min / 60)} sa${min % 60 ? ` ${min % 60} dk` : ''}` : `${min} dk`);

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Günaydın' : h < 18 ? 'İyi günler' : 'İyi akşamlar';
}

function dueLabel(due, today) {
  if (!due) return null;
  const d = dayDiff(due, today);
  if (d < 0) return { text: `${-d} gün gecikti`, cls: 'is-late' };
  if (d === 0) return { text: 'Bugün', cls: 'is-today' };
  if (d === 1) return { text: 'Yarın', cls: '' };
  return { text: new Date(`${due}T12:00:00Z`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', timeZone: 'UTC' }), cls: '' };
}

function NextMeeting({ m, email, now }) {
  if (!m) return null;
  const s = new Date(m.meeting_date); const e = endOf(m);
  const running = now >= s && now <= e;
  const mins = Math.round((s - now) / 60000);
  const when = running ? `şu an sürüyor · ${hm(e)}'e kadar`
    : mins < 60 ? `${mins} dk sonra`
      : s.toDateString() === now.toDateString() ? `bugün ${hm(s)}`
        : `${s.toLocaleDateString('tr-TR', { weekday: 'long' })} ${hm(s)}`;
  const join = joinState(m, now);
  return (
    <div className={`today-next ${running || join === 'now' ? 'is-soon' : ''}`}>
      <div className="today-next__label">{running ? 'Şu an' : 'Sıradaki toplantı'}</div>
      <div className="today-next__main">
        <div className="today-next__title">{m.title}</div>
        <div className="today-next__meta">{when}{m.account_company ? ` · ${m.account_company}` : ''}</div>
      </div>
      {m.meet_link && (
        <a className={`btn ${join === 'now' ? 'btn--primary' : 'btn--secondary'}`} href={joinUrl(m.meet_link, email)} target="_blank" rel="noopener noreferrer">
          <Icon name="video" size={14} /> Katıl
        </a>
      )}
    </div>
  );
}

function MeetingItem({ m, email, now, onPlay, faded }) {
  const s = new Date(m.meeting_date); const e = endOf(m);
  const past = e < now; const running = now >= s && now <= e;
  const join = joinState(m, now);
  return (
    <div className={`today-mtg ${past ? 'is-past' : ''} ${running ? 'is-running' : ''} ${faded ? 'is-faded' : ''}`}>
      <div className="today-mtg__time">{hm(s)}<span>{hm(e)}</span></div>
      <div className="today-mtg__main">
        {m.meet_link && !past ? (
          <a className="today-mtg__title is-link" href={joinUrl(m.meet_link, email)} target="_blank" rel="noopener noreferrer" title="Google Meet ile katıl">{m.title}</a>
        ) : m.fireflies_id && past ? (
          <button type="button" className="today-mtg__title is-link" onClick={() => onPlay(m)} title="Kaydı izle">{m.title}</button>
        ) : (
          <div className="today-mtg__title">{m.title}</div>
        )}
        <div className="today-mtg__meta">
          {m.account_company && <span className={`today-chip ${m.account_status === 'internal' ? 'is-internal' : ''}`}>{m.account_company}</span>}
          {running && <span className="today-live">Şu an</span>}
          {m.item_count > 0 && <span className="text-muted"><Icon name="clipboard" size={11} /> {m.item_count} madde</span>}
        </div>
      </div>
      <div className="today-mtg__act">
        {m.fireflies_id && past && (
          <button type="button" className="btn btn--secondary" onClick={() => onPlay(m)}><Icon name="play" size={11} /> Kayıt</button>
        )}
        {m.meet_link && !past && (
          <a className={`btn ${join === 'now' ? 'btn--primary' : 'btn--secondary'}`} href={joinUrl(m.meet_link, email)} target="_blank" rel="noopener noreferrer">
            <Icon name="video" size={12} /> Katıl
          </a>
        )}
      </div>
    </div>
  );
}

function TaskRow({ t, today, onDone, onTomorrow, onClient, onOpen, onPlayTask, busy }) {
  const due = dueLabel(t.due_date, today);
  const fresh = t.source_meeting_id && (Date.now() - new Date(t.created_at).getTime() < 3 * DAY_MS);
  return (
    <div className={`today-task ${busy ? 'is-busy' : ''}`}>
      <button type="button" className="today-task__check" onClick={() => onDone(t)} aria-label="Tamamlandı olarak işaretle" title="Tamamlandı" />
      <div className="today-task__main" onClick={() => onOpen(t)}>
        <div className="today-task__title">{t.title}</div>
        <div className="today-task__meta">
          {t.account_company && <span className="today-chip">{t.account_company}</span>}
          {due && <span className={`today-due ${due.cls}`}>{due.text}</span>}
          {t.status === 'in_progress' && <span className="today-due">Devam ediyor</span>}
          {t.source_meeting_id && t.source_has_recording && (
            <button type="button" className="today-mtgchip" onClick={(e) => { e.stopPropagation(); onPlayTask(t); }}
              title={`${t.source_meeting_title || 'Toplantı'} kaydını${t.source_ts != null ? ' görevin konuşulduğu andan' : ''} izle`}>
              <Icon name="play" size={9} /> toplantı
            </button>
          )}
          {fresh && <span className="today-new" title={t.source_meeting_title || ''}>Toplantıdan yeni</span>}
        </div>
      </div>
      <div className="today-task__act">
        <button type="button" onClick={() => onTomorrow(t)} title="Bitiş tarihini yarın yap">Yarına at</button>
        <button type="button" onClick={() => onClient(t)} title={t.status === 'on_client' ? 'Tekrar yapılacaklara al' : 'Müşteride bekliyor olarak işaretle'}>
          {t.status === 'on_client' ? 'Bizde' : 'Müşteride'}
        </button>
      </div>
    </div>
  );
}

export default function Today() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const isAdmin = can('today.view_others');
  const [data, setData] = useState(null);
  const [date, setDate] = useState(null);
  const [who, setWho] = useState('');
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState({});
  const [player, setPlayer] = useState(null);
  const [showLater, setShowLater] = useState(false);
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    if (date) q.set('date', date);
    if (who) q.set('user_id', who);
    try {
      const r = await api.get(`/me/today?${q}`);
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Sayfa yüklenemedi');
    }
  }, [date, who]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 5 * 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (isAdmin) api.get('/users').then(r => setUsers(r.data || [])).catch(() => {}); }, [isAdmin]);

  const today = data?.today;
  const day = data?.date;
  const isToday = day && day === today;

  // Görev grupları (bugüne göre)
  const groups = useMemo(() => {
    if (!data) return [];
    const g = { late: [], today: [], client: [], week: [], later: [] };
    for (const t of data.tasks) {
      const d = t.due_date ? dayDiff(t.due_date, data.today) : null;
      if (t.status === 'on_client') g.client.push(t);
      else if (d !== null && d < 0) g.late.push(t);
      else if (d === 0) g.today.push(t);
      else if (d !== null && d <= 7) g.week.push(t);
      else g.later.push(t);
    }
    return [
      { key: 'late', title: 'Gecikmiş', items: g.late, tone: 'late' },
      { key: 'today', title: 'Bugün', items: g.today, tone: 'today' },
      { key: 'client', title: 'Müşteride bekliyor', items: g.client, tone: 'client' },
      { key: 'week', title: 'Bu hafta', items: g.week },
      { key: 'later', title: 'Sonra / tarihsiz', items: g.later, collapsible: true },
    ];
  }, [data]);

  // Gün akışı: toplantılar + aralarındaki boş zaman (mesai içinde, bugünse sadece şu andan sonrası)
  const flow = useMemo(() => {
    if (!data) return [];
    const ms = [...data.meetings].sort((a, b) => new Date(a.meeting_date) - new Date(b.meeting_date));
    const base = new Date(`${data.date}T00:00:00`);
    let cursor = new Date(base.getTime() + WORK_START * 3600000);
    const endWork = new Date(base.getTime() + WORK_END * 3600000);
    if (data.date === data.today && now > cursor) cursor = new Date(Math.ceil(now.getTime() / 900000) * 900000);
    const out = [];
    const gap = (a, b) => { const min = Math.round((b - a) / 60000); if (min >= MIN_GAP) out.push({ gap: true, from: a, to: b, min }); };
    for (const m of ms) {
      const s = new Date(m.meeting_date); const e = endOf(m);
      if (s > cursor && cursor < endWork) gap(cursor, s < endWork ? s : endWork);
      out.push({ m });
      if (e > cursor) cursor = e;
    }
    if (cursor < endWork) gap(cursor, endWork);
    return out;
  }, [data, now]);

  const patch = async (t, body, msg, undoBody) => {
    setBusy(b => ({ ...b, [t.id]: true }));
    try {
      await api.put(`/tasks/${t.id}`, body);
      await load();
      toast((x) => (
        <span>{msg}
          {undoBody && <button type="button" className="today-undo" onClick={async () => { toast.dismiss(x.id); await api.put(`/tasks/${t.id}`, undoBody); load(); }}>Geri al</button>}
        </span>
      ), { duration: 5000 });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Görev güncellenemedi');
    } finally {
      setBusy(b => { const n = { ...b }; delete n[t.id]; return n; });
    }
  };
  const onDone = (t) => patch(t, { status: 'done' }, 'Görev tamamlandı', { status: t.status });
  const onTomorrow = (t) => patch(t, { due_date: addDays(today, 1) }, 'Yarına alındı', { due_date: t.due_date || null });
  const onClient = (t) => patch(t, { status: t.status === 'on_client' ? 'todo' : 'on_client' },
    t.status === 'on_client' ? 'Tekrar yapılacaklarda' : 'Müşteride bekliyor', { status: t.status });
  const onOpen = (t) => navigate(`/tasks?open=${t.id}`);
  // Görevin geldiği toplantının kaydı: görevin konuşulduğu saniyeden başlar
  const onPlayTask = async (t) => {
    try {
      const r = await api.get(`/meetings/${t.source_meeting_id}`);
      setPlayer({ meeting: r.data, startAt: t.source_ts != null ? Number(t.source_ts) : undefined });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Toplantı açılamadı');
    }
  };

  if (!data) return <div className="page-loading">Yükleniyor...</div>;
  const late = groups.find(g => g.key === 'late').items.length;
  const dueToday = groups.find(g => g.key === 'today').items.length;
  const viewer = data.user;
  const own = viewer.id === user?.id;

  return (
    <div className="today">
      <div className="page-header">
        <div>
          <h1 className="page-title">{own ? `${greeting()}, ${String(viewer.name || '').split(' ')[0]}` : `${viewer.name} · günü`}</h1>
          <p className="page-subtitle">
            {isToday ? 'Bugün' : longDate(day)} · {data.meetings.length} toplantı · {dueToday} görev bugün
            {late > 0 && <b className="today-latecount"> · {late} gecikmiş</b>}
            {data.done_today > 0 && <> · {data.done_today} tamamlandı</>}
          </p>
        </div>
        <div className="page-actions">
          {isAdmin && (
            <select className="form-input" style={{ width: 'auto' }} value={who} onChange={(e) => setWho(e.target.value)} aria-label="Kimin günü">
              <option value="">Benim günüm</option>
              {users.filter(u => u.id !== user?.id && u.is_active !== false).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          <div className="today-daynav">
            <button type="button" className="btn btn--secondary" onClick={() => setDate(addDays(day, -1))} aria-label="Önceki gün"><Icon name="chevronLeft" size={14} /></button>
            <button type="button" className="btn btn--secondary" onClick={() => setDate(null)} disabled={isToday}>Bugün</button>
            <button type="button" className="btn btn--secondary" onClick={() => setDate(addDays(day, 1))} aria-label="Sonraki gün"><Icon name="chevronRight" size={14} /></button>
          </div>
        </div>
      </div>

      {isToday && <NextMeeting m={data.next_meeting} email={user?.email} now={now} />}

      <div className="today-grid">
        <section className="card today-col">
          <h3 className="card__title">{isToday ? 'Bugünün akışı' : 'Günün akışı'}</h3>
          {flow.length === 0 && <div className="text-muted today-empty">Bu gün için toplantın yok</div>}
          {flow.map((x, i) => x.gap ? (
            <div key={`g${i}`} className="today-gap">{hm(x.from)}–{hm(x.to)} boş · {dur(x.min)}</div>
          ) : (
            <MeetingItem key={x.m.id} m={x.m} email={user?.email} now={now} onPlay={(m) => setPlayer({ meeting: m })} />
          ))}
          {data.brand_meetings.length > 0 && (
            <div className="today-brandmtg">
              <div className="today-sub">Sorumlu olduğun markalar <span className="text-muted">· davetli değilsin</span></div>
              {data.brand_meetings.map(m => (
                <MeetingItem key={m.id} m={m} email={user?.email} now={now} faded onPlay={(mm) => setPlayer({ meeting: mm })} />
              ))}
            </div>
          )}
        </section>

        <section className="card today-col">
          <h3 className="card__title">Görevlerim <span className="text-muted" style={{ fontWeight: 400 }}>· {data.tasks.length} açık</span></h3>
          {data.tasks.length === 0 && <div className="text-muted today-empty">Açık görevin yok</div>}
          {groups.filter(g => g.items.length).map(g => (
            <div key={g.key} className={`today-group ${g.tone ? `is-${g.tone}` : ''}`}>
              <button type="button" className="today-group__head" onClick={() => g.collapsible && setShowLater(v => !v)} disabled={!g.collapsible}>
                {g.title}<span>{g.items.length}</span>
                {g.collapsible && <Icon name={showLater ? 'chevronDown' : 'chevronRight'} size={12} />}
              </button>
              {(!g.collapsible || showLater) && g.items.map(t => (
                <TaskRow key={t.id} t={t} today={today} busy={!!busy[t.id]} onDone={onDone} onTomorrow={onTomorrow} onClient={onClient} onOpen={onOpen} onPlayTask={onPlayTask} />
              ))}
            </div>
          ))}
        </section>
      </div>

      {player && <MeetingVideoModal meeting={player.meeting} startAt={player.startAt} onClose={() => setPlayer(null)} onChanged={load} />}
    </div>
  );
}
